import { Injectable } from '@angular/core';
import { BehaviorSubject, timer, of } from 'rxjs';
import { switchMap, map, catchError, filter } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { DashboardService } from './dashboard.service';
import { NotificationWebSocketService } from './notification-websocket.service';
import { StampsService } from './stamps.service';
import { ProductStatusEnum } from '../../shared/enums/product-status.enum';

@Injectable({
  providedIn: 'root'
})
export class ApprovalCountService {
  private totalCountSubject = new BehaviorSubject<number>(0);
  public totalCount$ = this.totalCountSubject.asObservable();

  constructor(
    private authService: AuthService,
    private dashboardService: DashboardService,
    private notificationWebSocketService: NotificationWebSocketService,
    private stampsService: StampsService
  ) {
    // Listen to reactive metrics stream
    this.dashboardService.metrics$.pipe(
      filter(metrics => !!metrics),
      switchMap(metrics => {
        const baseCount = metrics.approvalsCount || 0;
        if (this.authService.isAdminOrHelpDesk()) {
          const queryParams = { page: 0, size: 1000, sort: 'id,desc' };
          return this.stampsService.getStamps(queryParams).pipe(
            map(res => {
              const list = res.Object_List || res.content || [];
              const pendingStamps = list.filter((s: any) => {
                const statusUpper = s.Status?.toUpperCase();
                return statusUpper === ProductStatusEnum.WAITING_CREATION_APPROVAL || 
                       statusUpper === ProductStatusEnum.WAITING_MODIFICATION_APPROVAL;
              });
              return baseCount + pendingStamps.length;
            }),
            catchError(err => {
              console.warn('[ApprovalCountService] Failed to load stamps count:', err);
              return of(baseCount);
            })
          );
        } else {
          return of(baseCount);
        }
      })
    ).subscribe(totalCount => {
      this.totalCountSubject.next(totalCount);
    });

    // Listen to real-time signals from WebSocket
    this.notificationWebSocketService.approvalCountSignals$.subscribe(signal => {
      const currentCount = this.totalCountSubject.value;
      console.log(`[ApprovalCountService] Received real-time signal: ${signal}. Current count: ${currentCount}`);
      if (signal === 'INCREMENT') {
        const newCount = currentCount + 1;
        console.log(`[ApprovalCountService] Incrementing count to: ${newCount}`);
        this.totalCountSubject.next(newCount);
      } else if (signal === 'DECREMENT' && currentCount > 0) {
        const newCount = currentCount - 1;
        console.log(`[ApprovalCountService] Decrementing count to: ${newCount}`);
        this.totalCountSubject.next(newCount);
      }
    });

    // Initial fetch and then poll every 5 minutes
    timer(0, 5 * 60 * 1000).pipe(
      filter(() => this.authService.isLoggedIn() && this.authService.isAdminOrHelpDesk()),
      switchMap(() => this.dashboardService.getMetrics().pipe(catchError(() => of(null))))
    ).subscribe();
  }

  refresh() {
    this.dashboardService.refreshMetrics();
  }
}
